import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { subDays } from "date-fns";
import { useCallback, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiResponse } from "@entrilia/shared/api/types";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useSetInterval from "@entrilia/shared/hooks/useSetInterval";
import { logError } from "@entrilia/shared/logging";
import { convertISODate, convertISODateTimeShort } from "@entrilia/shared/utilities/dateUtils";
import type { ObserverIssueInfo, ObserverIssueStatus, ObserverRunInfo } from "../../../api/observerTypes";
import { api } from "../../../api/operationsApi";
import UserContext from "../../../context/UserContext";
import LoadingFailedAlert from "../../common/LoadingFailedAlert";
import ObserverAcknowledgementDialog from "./ObserverAcknowledgementDialog";
import ObserverAssignPopover from "./ObserverAssignPopover";
import ObserverGithubNumberPopover from "./ObserverGithubNumberPopover";
import type { ObserverGridRow, ObserverIssueRow } from "./ObserverIssuesGrid";
import ObserverIssuesGrid from "./ObserverIssuesGrid";
import { getFileBugUrl, getInvestigatorUrl, resolveInvestigationState } from "./observerInvestigations";

interface ObserverPageData {
  run?: ObserverRunInfo;
  issues: ObserverIssueInfo[];
}

const recentIssuesLookbackDays = 14;
const observerPollingIntervalMs = 30000;

const setIssueStatus = withErrorHandling(api.observer.setIssueStatus);
const setIssueAcknowledgement = withErrorHandling(api.observer.setIssueAcknowledgement);
const setIssueAssignee = withErrorHandling(api.observer.setIssueAssignee);
const setIssueGithubNumber = withErrorHandling(api.observer.setIssueGithubNumber);

const serializeObserverPageData = (data: ObserverPageData) => JSON.stringify(data);

const issueMatchesSearch = (issue: ObserverIssueRow, term: string) =>
  [issue.service, issue.shortTitle, issue.exampleOperationId, issue.assignedToUserName, issue.assignedToUserEmail].some(
    (field) => field?.toLowerCase().includes(term)
  );

const ObserverIssuesPage = () => {
  const { runId } = useParams();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { userInfo } = useDefinedContext(UserContext);

  const [updatingIssueId, setUpdatingIssueId] = useState<string>();
  const [assigningIssue, setAssigningIssue] = useState<ObserverIssueRow>();
  const [assignAnchorEl, setAssignAnchorEl] = useState<HTMLElement | null>(null);
  const [acknowledgingIssue, setAcknowledgingIssue] = useState<ObserverIssueRow>();
  const [githubNumberIssue, setGithubNumberIssue] = useState<ObserverIssueRow>();
  const [githubNumberAnchorEl, setGithubNumberAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const pageDataSignatureRef = useRef<string | undefined>(undefined);
  const isPollingRef = useRef(false);

  const getObserverPageData = useCallback(
    async (signal?: AbortSignal): Promise<ApiResponse<ObserverPageData>> => {
      if (runId) {
        const response = await api.observer.getRun(runId, signal);
        return response.success
          ? { success: true, data: { run: response.data, issues: response.data.issues } }
          : { success: false, error: response.error };
      }

      const fromRunAtUtc = subDays(new Date(), recentIssuesLookbackDays).toISOString();
      const response = await api.observer.listIssues({ limit: 200, fromRunAtUtc }, signal);
      return response.success
        ? { success: true, data: { issues: response.data } }
        : { success: false, error: response.error };
    },
    [runId]
  );
  const getAssignableUsers = useCallback((signal?: AbortSignal) => api.observer.listAssignableUsers(signal), []);

  const [pageData, fetchError, { fetch, isFetching, setData: setPageData }] = useFetch(getObserverPageData, {
    onSetData: (data) => {
      pageDataSignatureRef.current = serializeObserverPageData(data);
    },
  });

  const [assignableUsers, assigneesError] = useFetch(getAssignableUsers);

  const loadError = fetchError ?? assigneesError;

  const issueRows = useMemo<ObserverIssueRow[]>(
    () =>
      (pageData?.issues ?? []).map((issue) => ({
        ...issue,
        rowType: "issue",
        runDay: convertISODate(issue.runAtUtc),
      })),
    [pageData?.issues]
  );

  const filteredIssueRows = useMemo<ObserverIssueRow[]>(() => {
    const term = search.trim().toLowerCase();
    return term ? issueRows.filter((issue) => issueMatchesSearch(issue, term)) : issueRows;
  }, [issueRows, search]);
  const gridRows = useMemo<ObserverGridRow[]>(() => {
    if (runId) {
      return filteredIssueRows;
    }

    const issueCountsByDay = filteredIssueRows.reduce((counts, issue) => {
      counts.set(issue.runDay, (counts.get(issue.runDay) ?? 0) + 1);
      return counts;
    }, new Map<string, number>());
    const emittedDays = new Set<string>();

    return filteredIssueRows.reduce<ObserverGridRow[]>((rows, issue) => {
      if (!emittedDays.has(issue.runDay)) {
        emittedDays.add(issue.runDay);
        rows.push({
          id: `observer-day-${issue.runDay}`,
          rowType: "day",
          runDay: issue.runDay,
          issueCount: issueCountsByDay.get(issue.runDay) ?? 0,
        });
      }

      rows.push(issue);
      return rows;
    }, []);
  }, [filteredIssueRows, runId]);

  const currentAssignableUser = useMemo(
    () =>
      assignableUsers?.find(
        (user) => user.userId === userInfo.id || user.email.toLowerCase() === userInfo.email.toLowerCase()
      ),
    [assignableUsers, userInfo.id, userInfo.email]
  );

  const handleSetStatus = useCallback(
    async (issueId: string, status: ObserverIssueStatus) => {
      setUpdatingIssueId(issueId);
      const [response, error] = await setIssueStatus(issueId, { status });
      setUpdatingIssueId(undefined);

      if (error || !response?.result) {
        if (error) {
          logError(error, "[ObserverIssuesPage] setIssueStatus");
        }
        sendNotificationError("Failed to update observer issue status.");
        return;
      }

      sendNotification("Observer issue status updated.");
      await fetch();
    },
    [fetch, sendNotification, sendNotificationError]
  );

  const handleCloseAcknowledgement = useCallback(() => setAcknowledgingIssue(undefined), []);

  const handleSaveAcknowledgement = useCallback(
    async (issue: ObserverIssueInfo, reason: string) => {
      setUpdatingIssueId(issue.id);
      const [response, error] = await setIssueAcknowledgement(issue.id, {
        expectedStatus: issue.status,
        acknowledgementReasonUserInput: reason,
      });
      setUpdatingIssueId(undefined);

      if (error || !response?.result) {
        if (error) {
          logError(error, "[ObserverIssuesPage] setIssueAcknowledgement");
        }
        sendNotificationError(error?.message || "Failed to save the acknowledgement.");
        return;
      }

      setAcknowledgingIssue(undefined);
      sendNotification("Acknowledgement saved.");
      await fetch();
    },
    [fetch, sendNotification, sendNotificationError]
  );

  const handleOpenAssign = useCallback((anchorEl: HTMLElement, issue: ObserverIssueRow) => {
    setAssignAnchorEl(anchorEl);
    setAssigningIssue(issue);
  }, []);

  const handleCloseAssign = useCallback(() => {
    setAssignAnchorEl(null);
    setAssigningIssue(undefined);
  }, []);

  const handleAssignIssue = useCallback(
    async (issue: ObserverIssueInfo, assignedToUserId?: string) => {
      handleCloseAssign();
      setUpdatingIssueId(issue.id);
      const [response, error] = await setIssueAssignee(issue.id, { assignedToUserId });
      setUpdatingIssueId(undefined);

      if (error || !response?.result) {
        if (error) {
          logError(error, "[ObserverIssuesPage] setIssueAssignee");
        }
        sendNotificationError("Failed to update observer issue assignee.");
        return;
      }

      sendNotification("Observer issue assignee updated.");
      await fetch();
    },
    [handleCloseAssign, fetch, sendNotification, sendNotificationError]
  );

  const handleReopenIssue = useCallback(
    async (issueId: string) => {
      setUpdatingIssueId(issueId);
      const [response, error] = await setIssueAssignee(issueId, { assignedToUserId: undefined });
      setUpdatingIssueId(undefined);

      if (error || !response?.result) {
        if (error) {
          logError(error, "[ObserverIssuesPage] reopen");
        }
        sendNotificationError("Failed to reopen observer issue.");
        return;
      }

      sendNotification("Observer issue reopened.");
      await fetch();
    },
    [fetch, sendNotification, sendNotificationError]
  );

  const openInvestigator = useCallback(
    (url: string) => {
      // window.open returns null whenever noopener is requested, which would hide a genuinely blocked pop-up.
      const opened = window.open(url, "_blank");
      if (!opened) {
        sendNotificationError("Allow pop-ups for this site to open the Production Investigator.");
        return;
      }
      opened.opener = null;
    },
    [sendNotificationError]
  );

  const handleInvestigate = useCallback(
    (issue: ObserverIssueRow) => {
      openInvestigator(getInvestigatorUrl(resolveInvestigationState(issue.investigations, userInfo.id), issue.id));
    },
    [userInfo.id, openInvestigator]
  );

  const handleFileBug = useCallback(
    (issue: ObserverIssueRow) => {
      openInvestigator(getFileBugUrl(issue.id));
    },
    [openInvestigator]
  );

  const handleOpenGithubNumber = useCallback((anchorEl: HTMLElement, issue: ObserverIssueRow) => {
    setGithubNumberAnchorEl(anchorEl);
    setGithubNumberIssue(issue);
  }, []);

  const handleCloseGithubNumber = useCallback(() => {
    setGithubNumberAnchorEl(null);
    setGithubNumberIssue(undefined);
  }, []);

  const handleSaveGithubNumber = useCallback(
    async (issue: ObserverIssueInfo, githubIssueNumber?: string) => {
      handleCloseGithubNumber();
      setUpdatingIssueId(issue.id);
      const [response, error] = await setIssueGithubNumber(issue.id, { githubIssueNumber });
      setUpdatingIssueId(undefined);

      if (error || !response?.result) {
        if (error) {
          logError(error, "[ObserverIssuesPage] setIssueGithubNumber");
        }
        sendNotificationError("Failed to update GitHub issue number.");
        return;
      }

      sendNotification("GitHub issue number updated.");
      await fetch();
    },
    [handleCloseGithubNumber, fetch, sendNotification, sendNotificationError]
  );

  const pollObserverPageData = useCallback(async () => {
    if (isPollingRef.current || isFetching || updatingIssueId) {
      return;
    }

    isPollingRef.current = true;
    const [response, error] = await withErrorHandling(getObserverPageData)();
    isPollingRef.current = false;

    if (error) {
      logError(error, "[ObserverIssuesPage] poll");
      return;
    }

    const nextSignature = serializeObserverPageData(response);
    if (pageDataSignatureRef.current === nextSignature) {
      return;
    }

    pageDataSignatureRef.current = nextSignature;
    setPageData(response);
  }, [getObserverPageData, isFetching, setPageData, updatingIssueId]);

  useSetInterval(pollObserverPageData, observerPollingIntervalMs, { disabled: !pageData });

  if (loadError) {
    return <LoadingFailedAlert apiError={loadError} />;
  }

  if (!pageData) {
    return <InlineLoader />;
  }

  const { run, issues } = pageData;
  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, minWidth: 0, py: 2.5, px: 3 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1">Production Observer</Typography>
          <Typography color="textSecondary">
            {run
              ? `${convertISODateTimeShort(run.runAtUtc)} · ${run.status} · ${issues.length} issues`
              : `${issues.length} issues from last ${recentIssuesLookbackDays} days`}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <SearchField onSearch={setSearch} debounceTimeMs={200} />
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => fetch()}
            loading={isFetching}
            startIcon={<RefreshRoundedIcon />}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>
      {run?.errorMessage && <Alert severity="error">{run.errorMessage}</Alert>}
      {run?.summaryHeadline && (
        <Alert severity={run.status === "Failed" ? "error" : "info"}>{run.summaryHeadline}</Alert>
      )}
      <ObserverIssuesGrid
        rows={gridRows}
        loading={isFetching}
        updatingIssueId={updatingIssueId}
        canAssign={Boolean(assignableUsers)}
        canEdit={Boolean(assignableUsers)}
        currentUserId={userInfo.id}
        onOpenAssign={handleOpenAssign}
        onOpenGithubNumber={handleOpenGithubNumber}
        onFileBug={handleFileBug}
        onSetStatus={handleSetStatus}
        onOpenAcknowledgement={setAcknowledgingIssue}
        onReopenIssue={handleReopenIssue}
        onInvestigate={handleInvestigate}
      />
      {assignAnchorEl && assigningIssue && (
        <ObserverAssignPopover
          anchorEl={assignAnchorEl}
          issue={assigningIssue}
          assignableUsers={assignableUsers}
          currentUser={currentAssignableUser}
          onAssign={handleAssignIssue}
          onClose={handleCloseAssign}
        />
      )}
      {acknowledgingIssue && (
        <ObserverAcknowledgementDialog
          issue={acknowledgingIssue}
          saving={updatingIssueId === acknowledgingIssue.id}
          onSave={handleSaveAcknowledgement}
          onClose={handleCloseAcknowledgement}
        />
      )}
      {githubNumberAnchorEl && githubNumberIssue && (
        <ObserverGithubNumberPopover
          anchorEl={githubNumberAnchorEl}
          issue={githubNumberIssue}
          onSave={handleSaveGithubNumber}
          onClose={handleCloseGithubNumber}
        />
      )}
    </Stack>
  );
};

export default ObserverIssuesPage;
